import React, { createContext, useContext, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSchool, useAuthStore } from '@sportschool/shared-logic';
import type { SchoolResponse } from '@sportschool/shared-types';
import { setDisplayTimeZone } from '@/lib/utils';

export interface SchoolContextType {
  school: SchoolResponse | null;
  currency: string;
  timeZone: string;
  enabledFeatures: string[];
  subscriptionBillingDay: number;
  formatPrice: (amount: number | null | undefined) => string;
  perMonthLabel: string;
  isLoading: boolean;
}

const defaultContextValue: SchoolContextType = {
  school: null,
  currency: 'PLN',
  timeZone: 'Europe/Warsaw',
  enabledFeatures: [],
  subscriptionBillingDay: 1,
  formatPrice: (amount) => `${(amount ?? 0).toFixed(2)} PLN`,
  perMonthLabel: '/ mies.',
  isLoading: false,
};

const SchoolContext = createContext<SchoolContextType>(defaultContextValue);

export function SchoolProvider({ children }: { children: React.ReactNode }) {
  const { schoolId: paramSchoolId } = useParams<{ schoolId?: string }>();
  const userContext = useAuthStore((s) => s.userContext);
  const schoolId = paramSchoolId || userContext?.activeSchool?.id || '';

  const { data: school, isLoading } = useSchool(schoolId);
  const { t } = useTranslation();

  const value = useMemo<SchoolContextType>(() => {
    const currency = school?.currency || 'PLN';
    const timeZone = school?.timezone || 'Europe/Warsaw';
    setDisplayTimeZone(timeZone);
    const rawFeatures = school?.enabledFeatures ?? userContext?.activeSchool?.enabledFeatures ?? [];
    const enabledFeatures: string[] = Array.isArray(rawFeatures)
      ? rawFeatures
      : typeof rawFeatures === 'string'
      ? rawFeatures.split(',').map((f) => f.trim()).filter(Boolean)
      : [];
    const subscriptionBillingDay = school?.subscriptionBillingDay ?? 1;

    const perMonthLabel = t('common.perMonth', '/ mies.');

    const formatPrice = (amount: number | null | undefined) => {
      const val = amount ?? 0;
      return `${val.toFixed(2)} ${currency}`;
    };

    return {
      school: school ?? null,
      currency,
      timeZone,
      enabledFeatures,
      subscriptionBillingDay,
      formatPrice,
      perMonthLabel,
      isLoading,
    };
  }, [school, userContext, t, isLoading]);

  return <SchoolContext.Provider value={value}>{children}</SchoolContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useSchoolContext() {
  return useContext(SchoolContext);
}
